import { useQuery } from "@tanstack/react-query";

import { getFormattedSuggestionName } from "@app/components/search-form/helpers";
import {
  getFormattedQueryParams,
  getNapiQueryParams,
  getOriginPlaceIds,
  getSearchTypes,
  isSearchTypeInvalid
} from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/flex-query-params";
import type { LocationStateValues } from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/use-search-state-hooks";
import { useOriginLocation } from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/use-search-state-hooks";
import { search_form_widget_state } from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/use-search-widget-state";
import { trackInvalidCityError } from "@app/components/search-form-hydrated/search-form-errors";
import { useLiteAppContext } from "@app/helpers/hooks";
import { lazySentryCaptureException } from "@app/helpers/sentry-capture";
import type { PlacesResponse } from "@app/modules/search/autocomplete/autocomplete-client";
import type { FormattedSuggestion } from "@app/modules/search/helpers/format-suggestion";
import { formatSuggestions } from "@app/modules/search/helpers/format-suggestion";
import type { Features } from "@app/types/experiments";
import type { SearchLocationType, SearchPlace } from "@app/types/search-types";

import type { SupportedLocale } from "@config/locales";

const FlexEndpoints = {
  PointOfInterestSuggestion: "/flex/suggestions/points-of-interest"
};

const ACCEPT_HEADER_V2 =
  "application/vnd.busbud+json; version=2; profile=https://schema.busbud.com/v2/anything.json";

export interface NapiFlexLocationOption {
  value: FormattedSuggestion;
  label: string;
  id: string;
}

export interface AutocompletePredictionDataset {
  location_options: NapiFlexLocationOption[];
}

interface GetNapiQueryPredictionsParams {
  input_type: SearchLocationType;
  query: string;
  features: Partial<Features>;
  locale: SupportedLocale;
}

const features_params: readonly string[] = [
  /** enables search via query parameter **/
  `${encodeURIComponent("use_route_metrics")}=${encodeURIComponent(true)}`,
  /** removes locations from autocomplete **/
  `${encodeURIComponent("exclude_locations")}=${encodeURIComponent(true)}`
];

const getNapiQueryPredictions = async ({
  input_type,
  query,
  features,
  locale
}: GetNapiQueryPredictionsParams) => {
  if (typeof window === "undefined") {
    throw new Error(
      "getNapiQueryPredictions should only be called in the browser"
    );
  }

  const napi_url = window.BB.NAPI_URL; // TODO - move to context
  const url = `${napi_url}${FlexEndpoints.PointOfInterestSuggestion}`;
  const search_state = window.store.getState()
    .search_form as LocationStateValues;

  const { latitude, longitude } = window.BB.search_form; // TODO - move to context
  const is_geo_available: boolean = !!latitude && !!longitude;

  const search_types = getSearchTypes({
    query,
    input_type,
    search_state,
    is_geo_available,
    features
  });

  if (isSearchTypeInvalid(search_types)) {
    return {
      formatted_suggestions: [],
      search_types,
      number_of_suggestions: 0
    };
  }

  const params = getNapiQueryParams({
    query,
    input_type,
    search_types,
    search_state,
    lang: window.BB.config.lang, // TODO - move to context
    locale,
    latitude,
    longitude
  });

  const whitelabel_name = window.BB.whitelabel_context?.whitelabel_name;
  if (search_form_widget_state.is_web_component && whitelabel_name) {
    Object.assign(params, {
      whitelabel: whitelabel_name
    });
  }

  const qs = getFormattedQueryParams(params).concat(features_params).join("&");

  const data = await fetch(`${url}?${qs}`, {
    headers: {
      Accept: ACCEPT_HEADER_V2,
      "x-busbud-token": window.BB.NAPI_AUTH_TOKEN, // TODO - move to context
      "x-request-id": window.BB.inst.request_id // TODO - move to context
    }
  })
    .then(response => response.json() as Promise<PlacesResponse>)
    .catch(err => {
      const status_code: number = err.response ? err.response.status : 0;
      const reason: string | undefined = err.message ? err.message : undefined;
      const request_id = err.response?.headers
        ? String(err.response.headers["x-request-id"])
        : undefined;

      lazySentryCaptureException(err, {
        tags: {
          component: "AutocompleteInputHydrated",
          request: "get_napi_query_predictions"
        },
        extra: {
          url,
          status_code,
          request_id,
          reason
        }
      });

      return {
        suggestions: [],
        parents: {},
        siblings: {}
      } satisfies PlacesResponse;
    });
  const suggestions: NapiFlexLocationOption[] = formatSuggestions(data).map(
    formatted_suggestion => ({
      id: getFormattedPlaceUniqueId(formatted_suggestion),
      value: formatted_suggestion,
      label: getFormattedSuggestionName(formatted_suggestion)
    })
  );

  if (query.trim().length > 0 && suggestions.length === 0) {
    trackInvalidCityError({
      query,
      search_types,
      latitude,
      longitude
    });
  }

  return {
    formatted_suggestions: suggestions,
    search_types,
    number_of_suggestions: suggestions.length
  };
};

export const fallback_autocomplete_data: AutocompletePredictionDataset = {
  location_options: []
};

interface PredictionQueryKeyParams {
  input_type: SearchLocationType;
  query: string;
  origin_search_place: SearchPlace | null;
}

const getPredictionQueryKey = ({
  input_type,
  query,
  origin_search_place
}: PredictionQueryKeyParams): [string, SearchLocationType, string, string] => {
  const is_destination_autocomplete = input_type === "destination";
  let origin_ids = "";

  if (is_destination_autocomplete) {
    const { origin_id, origin_child_id } =
      getOriginPlaceIds(origin_search_place);
    origin_ids = `${origin_id}:${origin_child_id}`;
  }

  return ["autosuggestions", input_type, query, origin_ids];
};

export const useNapiPredictions = (
  input_type: SearchLocationType,
  query: string
) => {
  const { features, locale } = useLiteAppContext();
  const origin = useOriginLocation();

  return useQuery<AutocompletePredictionDataset>({
    queryKey: getPredictionQueryKey({
      input_type,
      query,
      origin_search_place: origin
    }),
    queryFn: async (): Promise<AutocompletePredictionDataset> => {
      try {
        const { formatted_suggestions } = await getNapiQueryPredictions({
          input_type,
          query,
          features,
          locale
        });
        return {
          location_options: formatted_suggestions
        };
      } catch (err) {
        lazySentryCaptureException(err);
        throw err;
      }
    },
    placeholderData: (
      previousData,
      _previousQuery
    ): AutocompletePredictionDataset =>
      previousData || fallback_autocomplete_data
  });
};

export function getFormattedPlaceUniqueId(place: FormattedSuggestion): string {
  return place.location?.geo_entity_id ?? place.geo_entity_id ?? place.id;
}
